import { AuthPage } from "@/components/Auth/AuthPage";
import { DashboardPage } from "@/components/Dashboard/DashboardPage";
import { ApplicationsPage } from "@/components/Applications/ApplicationsPage";
import { ApplicationDetailPage } from "@/components/Applications/ApplicationDetailPage";
import { Layout } from "@/components/Layout/Layout";
import { MetricsPage } from "@/components/Metrics/MetricsPage";
import { OrganizationsPage } from "@/components/Organizations/OrganizationsPage";
import { PlansPage } from "@/components/Plans/PlansPage";
import { SettingsPage } from "@/components/Settings/SettingsPage";
import { TeamManagementPage } from "@/components/Teams/TeamManagementPage";
import { Toaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "./App.css";
import Loading from "./components/Common/Loading";
import ProtectedRoute from "./components/Common/ProtectedRoute";
import { CreateDeployPage } from "./components/Deploy/CreateDeployPage";
import GitHubApp from "./components/GitHubApp/GitHubApp";
import OAuthGitHubFinish from "./components/GitHubApp/OAuthGitHubFinish";
import { QueryBoundary } from "./components/Layout/QueryBoundary";
import { ProjectsPage } from "./components/Projects/ProjectsPage";
import { AuthProvider } from "./contexts/AuthContext";
import { PATH } from "./lib/enums";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function AppContent() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to={PATH.HOME} replace />} />
          <Route
            path={`${PATH.APPLICATIONS}/:id`}
            element={<ApplicationDetailPage />}
          />
          <Route path={PATH.APPLICATIONS} element={<ApplicationsPage />} />
          <Route path={PATH.PROJECTS} element={<ProjectsPage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path={PATH.CREATE_DEPLOY} element={<CreateDeployPage />} />
          <Route path="organizations" element={<OrganizationsPage />} />
          <Route path="plans" element={<PlansPage />} />
          <Route path="metrics" element={<MetricsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="teams" element={<TeamManagementPage />} />
          <Route path="github" element={<GitHubApp />} />
          <Route path="oauth/github/finish" element={<OAuthGitHubFinish />} />
        </Route>
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
      </Routes>
      <Toaster />
    </div>
  );
}

function App() {
  const baseName = import.meta.env.BASE_URL.replace(/\/$/, "") || "/";

  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<Loading />}>
        <BrowserRouter basename={baseName}>
          <AuthProvider>
            <QueryBoundary>
              <AppContent />
            </QueryBoundary>
          </AuthProvider>
        </BrowserRouter>
      </Suspense>
    </QueryClientProvider>
  );
}

export default App;
